import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  ArrowLeft, Edit2, DollarSign, Clock, Calendar, Save, X,
  TrendingUp, Plus, Trash2, Pencil, Check, ChevronDown, ChevronUp, ChevronRight, UserX, UserCheck,
  Calculator, Info, AlertTriangle
} from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { todayLocalISO } from "../lib/utils";

// Flag time entries whose recorded worked_hours materially exceeds the
// physical clock-in → clock-out window. Employees sometimes bump the
// number after the fact; owners/managers get a subtle badge on the row
// and a live warning banner in the edit dialog. 0.05h ≈ 3 min tolerance
// for legitimate minute-level rounding.
const OVER_CLOCK_TOLERANCE_HOURS = 0.05;
const clockSpanHours = (entry) => {
  if (!entry?.check_in || !entry?.check_out) return null;
  const inMs = new Date(entry.check_in).getTime();
  const outMs = new Date(entry.check_out).getTime();
  if (!Number.isFinite(inMs) || !Number.isFinite(outMs) || outMs <= inMs) return null;
  return (outMs - inMs) / 3600000;
};
const isOverClock = (entry) => {
  const span = clockSpanHours(entry);
  if (span == null) return false;
  return parseFloat(entry?.worked_hours || 0) > span + OVER_CLOCK_TOLERANCE_HOURS;
};

const MonthGroup = ({ label, totalNet, totalWorked, totalBreak, entries, canOverride, formatDate, openEditTimeEntry, setDeleteDialog, highlightId }) => {
  // Auto-expand when the deep-link target lives inside this month so a
  // click from Audit Log doesn't leave the user staring at a collapsed
  // header.
  const containsHighlight = !!highlightId && entries.some(e => e.entry_id === highlightId);
  const [expanded, setExpanded] = useState(containsHighlight);
  useEffect(() => {
    if (containsHighlight) setExpanded(true);
  }, [containsHighlight]);
  return (
    <div className="border-b border-slate-100 last:border-b-0" data-testid={`month-group-${label}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-6 py-3 hover:bg-slate-50/80 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
        data-testid={`month-toggle-${label}`}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <span className="font-semibold text-[#0A192F]">{label}</span>
          <Badge variant="outline" className="text-slate-500">{entries.length} entries</Badge>
        </div>
        <div className="flex items-center gap-6 text-sm">
          <span className="text-slate-500">Worked: <span className="font-medium text-[#0A192F]">{totalWorked.toFixed(1)}h</span></span>
          <span className="text-slate-500">Break: <span className="font-medium text-slate-600">{totalBreak.toFixed(1)}h</span></span>
          <span className="text-slate-500">Net: <span className="font-bold text-[#0D9488]">{totalNet.toFixed(1)}h</span></span>
        </div>
      </button>
      {expanded && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/60 hover:bg-slate-50/60">
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold pl-12">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Clock In/Out</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Worked Hours</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Break</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Net Hours</TableHead>
                {canOverride && <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow key={entry.entry_id} data-testid="time-entry-row" data-highlight-id={entry.entry_id}>
                  <TableCell className="font-medium pl-12">{formatDate(entry.worked_date)}</TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {entry.check_in ? (
                      <span>
                        {new Date(entry.check_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        <span className="text-slate-300 mx-1">→</span>
                        {entry.check_out ? new Date(entry.check_out).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—'}
                        {entry.source === 'clock_auto' && <Badge variant="outline" className="ml-1 text-[10px] text-amber-500 border-amber-300">Auto</Badge>}
                      </span>
                    ) : (
                      <span className="text-slate-300">Manual</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      {entry.worked_hours} hrs
                      {isOverClock(entry) && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-300 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                          title={`Worked hours (${entry.worked_hours}h) exceed the clock-in/out span (${clockSpanHours(entry).toFixed(2)}h). Manual edit or forgotten clock-out.`}
                          data-testid={`over-clock-badge-${entry.entry_id}`}
                        >
                          <AlertTriangle className="w-3 h-3" />
                          +{(parseFloat(entry.worked_hours) - clockSpanHours(entry)).toFixed(2)}h
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>{entry.break_hours} hrs</TableCell>
                  <TableCell className="font-medium text-[#0D9488]">{entry.net_hours} hrs</TableCell>
                  {canOverride && (
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" data-testid={`edit-time-entry-${entry.entry_id}`} onClick={() => openEditTimeEntry(entry)}>
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`delete-time-entry-${entry.entry_id}`} onClick={() => setDeleteDialog({ open: true, type: "time-entry", id: entry.entry_id, label: `time entry for ${formatDate(entry.worked_date)} (${entry.net_hours} hrs)` })}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

const EmployeeDetail = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const canOverride = user?.role === "owner" || user?.role === "manager";
  // Pay-history edit/delete + pay-type override are owner-only — these
  // mutate historical earnings, so we don't want managers doing them.
  const isOwner = user?.role === "owner";

  // Deep-link support: `?tab=time|payments|deductions|...` (from Audit Log
  // rows). Falls back to `details`. `?highlight=<record_id>` scrolls the
  // matching row into view once the tab renders.
  const ALLOWED_TABS = ["details", "pay-history", "pay-periods", "time", "payments", "deductions"];
  const requestedTab = searchParams.get("tab");
  const initialTab = ALLOWED_TABS.includes(requestedTab) ? requestedTab : "details";
  const [activeTab, setActiveTab] = useState(initialTab);
  const highlightId = searchParams.get("highlight") || null;

  // Sync tab state back to URL so browser Back/Forward round-trips and
  // Audit Log deep links share the same source of truth.
  useEffect(() => {
    const current = searchParams.get("tab");
    if (activeTab === "details") {
      if (current) {
        const next = new URLSearchParams(searchParams);
        next.delete("tab");
        setSearchParams(next, { replace: true });
      }
    } else if (current !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab]);

  const [employee, setEmployee] = useState(null);
  const [payGroups, setPayGroups] = useState([]);
  const [scheduleGroups, setScheduleGroups] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payHistory, setPayHistory] = useState([]);
  const [payPeriods, setPayPeriods] = useState(null);
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  // Pay History dialog state
  const [showPayHikeDialog, setShowPayHikeDialog] = useState(false);
  const [savingPayHike, setSavingPayHike] = useState(false);
  const [editingPayHistoryId, setEditingPayHistoryId] = useState(null);
  const [payHikeData, setPayHikeData] = useState({
    pay_type: "", pay_value: "", effective_date: todayLocalISO(), reason: ""
  });

  // Time entry edit state
  const [showTimeEditDialog, setShowTimeEditDialog] = useState(false);
  const [savingTimeEdit, setSavingTimeEdit] = useState(false);
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [timeEditData, setTimeEditData] = useState({
    worked_date: "", worked_hours: "", break_hours: ""
  });

  // Payment edit state
  const [showPaymentEditDialog, setShowPaymentEditDialog] = useState(false);
  const [savingPaymentEdit, setSavingPaymentEdit] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPeriodSelectValue, setEditPeriodSelectValue] = useState("");
  const [paymentEditData, setPaymentEditData] = useState({
    amount_paid: "", payment_method: "", payment_date: "", extra_amount: "", deduction_amount: "", notes: ""
  });

  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: "", id: "", label: "" });
  const [deleting, setDeleting] = useState(false);

  // Inline pay period payment state
  const [expandedPeriod, setExpandedPeriod] = useState(null); // index of expanded period
  const [periodPayData, setPeriodPayData] = useState({
    payment_date: todayLocalISO(),
    payment_method: "cash",
    amount_paid: "",
    deduction_amount: "0",
    deduction_reason: "",
    notes: ""
  });
  const [savingPeriodPay, setSavingPeriodPay] = useState(false);

  // Termination state
  const [showTerminateDialog, setShowTerminateDialog] = useState(false);
  const [terminationDate, setTerminationDate] = useState(todayLocalISO());
  const [savingTermination, setSavingTermination] = useState(false);

  // Deductions dialog state
  const [showDeductionDialog, setShowDeductionDialog] = useState(false);
  const [editingDeductionId, setEditingDeductionId] = useState(null);
  const [savingDeduction, setSavingDeduction] = useState(false);
  const [deductionForm, setDeductionForm] = useState({
    period_key: "",
    pay_period_start: "",
    pay_period_end: "",
    basis: "custom",        // custom | days | half_days | hours
    units: "",              // day/half-day/hour count for non-custom basis
    amount: "",
    reason: "",
  });
  const [deductionPreview, setDeductionPreview] = useState(null);

  // Balance Audit dialog
  const [showBalanceAudit, setShowBalanceAudit] = useState(false);
  const [balanceAudit, setBalanceAudit] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  useEffect(() => { fetchData(); }, [employeeId]);

  // After data loads (or when Audit Log deep-links change the highlight
  // target), scroll the matching row into view and briefly flash it. Rows
  // opt in by setting `data-highlight-id={record_id}`. Uses the DOM (not
  // refs) so it works uniformly across Time Entries / Payments /
  // Deductions / Pay Rate History tabs without threading refs everywhere.
  useEffect(() => {
    if (!highlightId || loading) return;
    // Give the tab a tick to mount + expand any collapsible month groups.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-highlight-id="${highlightId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
        setTimeout(() => {
          el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2");
        }, 2500);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [highlightId, loading, activeTab, timeEntries, payments, deductions, payHistory]);

  const fetchData = async () => {
    try {
      const [empRes, pgRes, sgRes, teRes, payRes, historyRes, periodsRes, dedRes] = await Promise.all([
        apiClient.get(`/employees/${employeeId}`),
        apiClient.get('/pay-groups'),
        apiClient.get('/schedule-groups'),
        apiClient.get(`/time-entries?employee_id=${employeeId}`),
        apiClient.get(`/payments?employee_id=${employeeId}`),
        apiClient.get(`/employees/${employeeId}/pay-history`),
        apiClient.get(`/employees/${employeeId}/pay-periods`),
        apiClient.get(`/employees/${employeeId}/deductions`),
      ]);
      setEmployee(empRes.data.employee);
      setPayGroups(pgRes.data.pay_groups);
      setScheduleGroups(sgRes.data.schedule_groups);
      setTimeEntries(teRes.data.time_entries);
      setPayments(payRes.data.payments);
      setPayHistory(historyRes.data.pay_history || []);
      setPayPeriods(periodsRes.data);
      setDeductions(dedRes.data.deductions || []);
      setFormData(empRes.data.employee);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // ---- Pay History handlers ----
  const handleSavePayHike = async () => {
    setSavingPayHike(true);
    try {
      const payload = {
        // When the owner explicitly unlocks "Override pay type" we honor
        // the dropdown value; otherwise we force the employee's current
        // pay_type. Defense in depth: even if a future UI bug leaks a
        // stale value into payHikeData.pay_type, the lock holds unless
        // the override checkbox was deliberately ticked.
        pay_type: payHikeData.unlock_pay_type
          ? (payHikeData.pay_type || employee.pay_type)
          : employee.pay_type,
        pay_value: parseFloat(payHikeData.pay_value),
        effective_date: payHikeData.effective_date,
        reason: payHikeData.reason || undefined
      };
      if (editingPayHistoryId) {
        await apiClient.put(`/employees/${employeeId}/pay-history/${editingPayHistoryId}`, payload);
      } else {
        await apiClient.post(`/employees/${employeeId}/pay-history`, payload);
      }
      closePayHikeDialog();
      fetchData();
    } catch (error) {
      console.error("Failed to save pay change:", error);
      toast.error(error.response?.data?.detail || "Failed to save pay rate change");
    } finally {
      setSavingPayHike(false);
    }
  };

  const openEditPayHistory = (entry) => {
    setEditingPayHistoryId(entry.history_id);
    // Pre-tick "Override pay type" when this historical entry already differs
    // from the employee's current pay_type — otherwise the dropdown would be
    // locked to today's type and silently overwrite the historical record.
    const differsFromCurrent = employee && entry.pay_type && entry.pay_type !== employee.pay_type;
    setPayHikeData({
      pay_type: entry.pay_type,
      pay_value: String(entry.pay_value),
      effective_date: entry.effective_date,
      reason: entry.reason || "",
      unlock_pay_type: Boolean(differsFromCurrent),
    });
    setShowPayHikeDialog(true);
  };

  const closePayHikeDialog = () => {
    setShowPayHikeDialog(false);
    setEditingPayHistoryId(null);
    setPayHikeData({ pay_type: "", pay_value: "", effective_date: todayLocalISO(), reason: "", unlock_pay_type: false });
  };

  // ---- Time Entry handlers ----
  const openEditTimeEntry = (entry) => {
    setEditingTimeEntry(entry);
    setTimeEditData({
      worked_date: entry.worked_date,
      worked_hours: String(entry.worked_hours),
      break_hours: String(entry.break_hours)
    });
    setShowTimeEditDialog(true);
  };

  const handleSaveTimeEdit = async () => {
    setSavingTimeEdit(true);
    try {
      await apiClient.put(`/time-entries/${editingTimeEntry.entry_id}`, {
        worked_date: timeEditData.worked_date,
        worked_hours: parseFloat(timeEditData.worked_hours),
        break_hours: parseFloat(timeEditData.break_hours)
      });
      setShowTimeEditDialog(false);
      setEditingTimeEntry(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update time entry:", error);
      toast.error(error.response?.data?.detail || "Failed to update time entry");
    } finally {
      setSavingTimeEdit(false);
    }
  };

  // ---- Payment handlers ----
  const openEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentEditData({
      amount_paid: String(payment.amount_paid),
      payment_method: payment.payment_method,
      payment_date: payment.payment_date,
      pay_period_start: payment.pay_period_start || "",
      pay_period_end: payment.pay_period_end || "",
      extra_amount: String(payment.extra_amount || 0),
      deduction_amount: String(payment.deduction_amount || 0),
      notes: payment.notes || ""
    });
    // Pre-select the matching canonical period in the dropdown if the payment
    // already aligns with one. Otherwise default to "__custom__" so the
    // existing dates remain visible/editable as a starting point.
    const ps = payment.pay_period_start || "";
    const pe = payment.pay_period_end || "";
    const periods = payPeriods?.periods || [];
    const matched = periods.find(p => p.period_start === ps && p.period_end === pe);
    setEditPeriodSelectValue(matched ? `${ps}|${pe}` : "__custom__");
    setShowPaymentEditDialog(true);
  };

  const handleEditPeriodSelect = (value) => {
    setEditPeriodSelectValue(value);
    if (value === "__custom__") {
      // Keep whatever is currently in the form so owner can edit by hand.
      return;
    }
    const [ps, pe] = value.split("|");
    // Update ONLY the period fields on edit. The amount paid is a historical
    // fact — re-labeling the period must never silently change the dollar
    // amount (user-reported bug: auto-fill was overwriting $x with $y).
    setPaymentEditData(prev => ({
      ...prev,
      pay_period_start: ps,
      pay_period_end: pe,
    }));
  };

  const handleSavePaymentEdit = async () => {
    setSavingPaymentEdit(true);
    try {
      await apiClient.put(`/payments/${editingPayment.payment_id}`, {
        amount_paid: parseFloat(paymentEditData.amount_paid),
        payment_method: paymentEditData.payment_method,
        payment_date: paymentEditData.payment_date,
        pay_period_start: paymentEditData.pay_period_start,
        pay_period_end: paymentEditData.pay_period_end,
        extra_amount: parseFloat(paymentEditData.extra_amount || 0),
        deduction_amount: parseFloat(paymentEditData.deduction_amount || 0),
        notes: paymentEditData.notes
      });
      setShowPaymentEditDialog(false);
      setEditingPayment(null);
      fetchData();
    } catch (error) {
      console.error("Failed to update payment:", error);
      toast.error(error.response?.data?.detail || "Failed to update payment");
    } finally {
      setSavingPaymentEdit(false);
    }
  };

  // ---- Generic delete handler ----
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { type, id } = deleteDialog;
      if (type === "employee") {
        await apiClient.delete(`/employees/${id}`);
        setDeleteDialog({ open: false, type: "", id: "", label: "" });
        navigate('/employees');
        return;
      } else if (type === "pay-history") {
        await apiClient.delete(`/employees/${employeeId}/pay-history/${id}`);
      } else if (type === "time-entry") {
        await apiClient.delete(`/time-entries/${id}`);
      } else if (type === "payment") {
        await apiClient.delete(`/payments/${id}`);
      } else if (type === "deduction") {
        await apiClient.delete(`/deductions/${id}`);
      }
      setDeleteDialog({ open: false, type: "", id: "", label: "" });
      fetchData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error(error.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  // ---- Employee save ----
  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/employees/${employeeId}`, {
        name: formData.name, email: formData.email, phone: formData.phone,
        status: formData.status, pay_group_id: formData.pay_group_id,
        pay_type: formData.pay_type, pay_value: parseFloat(formData.pay_value),
        hire_date: formData.hire_date, schedule_group_id: formData.schedule_group_id || "",
        pin: formData.pin || "", notes: formData.notes,
        // Only forward termination_date when the employee is terminated AND
        // the date actually changed. Sending an empty value would reactivate
        // them silently — Reactivate must be an explicit user action.
        ...(employee.termination_date && formData.termination_date && formData.termination_date !== employee.termination_date
          ? { termination_date: formData.termination_date }
          : {}),
      });
      setEditing(false);
      fetchData();
    } catch (error) {
      console.error("Failed to update employee:", error);
    } finally {
      setSaving(false);
    }
  };

  // ---- Terminate employee ----
  const handleTerminate = async () => {
    setSavingTermination(true);
    try {
      await apiClient.put(`/employees/${employeeId}`, {
        termination_date: terminationDate
      });
      toast.success("Employee terminated successfully");
      setShowTerminateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to terminate employee");
    } finally {
      setSavingTermination(false);
    }
  };

  // ---- Reactivate employee ----
  const handleReactivate = async () => {
    try {
      await apiClient.put(`/employees/${employeeId}`, {
        termination_date: ""
      });
      toast.success("Employee reactivated successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate employee");
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    return new Date(dateOnly + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getPayGroupName = (id) => payGroups.find(g => g.pay_group_id === id)?.group_name || "-";

  const togglePeriodPay = (index, period) => {
    if (expandedPeriod === index) {
      setExpandedPeriod(null);
      return;
    }
    setExpandedPeriod(index);
    setPeriodPayData({
      payment_date: todayLocalISO(),
      payment_method: "cash",
      amount_paid: String(period.balance > 0 ? period.balance : period.amount_due),
      deduction_amount: "0",
      deduction_reason: "",
      notes: ""
    });
  };

  const handlePeriodPaySubmit = async (period) => {
    setSavingPeriodPay(true);
    try {
      await apiClient.post('/payments', {
        employee_id: employeeId,
        pay_period_start: period.period_start,
        pay_period_end: period.period_end,
        payment_date: periodPayData.payment_date,
        payment_method: periodPayData.payment_method,
        amount_paid: parseFloat(periodPayData.amount_paid),
        deduction_amount: parseFloat(periodPayData.deduction_amount || 0),
        deduction_reason: periodPayData.deduction_reason || undefined,
        notes: periodPayData.notes || undefined
      });
      setExpandedPeriod(null);
      fetchData();
    } catch (error) {
      console.error("Failed to record payment:", error);
      toast.error(error.response?.data?.detail || "Failed to record payment");
    } finally {
      setSavingPeriodPay(false);
    }
  };

  // ---- Deductions handlers ----
  const openDeductionDialog = (deduction = null) => {
    if (deduction) {
      // Preserve the original `basis` + `units` when they were stored
      // (deductions saved via days/half_days/hours) so the form shows the
      // right controls with the existing values. Custom-amount deductions
      // fall back to `basis: "custom"` so the $ input is shown pre-filled.
      const savedBasis = deduction.basis || "custom";
      setEditingDeductionId(deduction.deduction_id);
      setDeductionForm({
        period_key: `${deduction.pay_period_start}|${deduction.pay_period_end}`,
        pay_period_start: deduction.pay_period_start,
        pay_period_end: deduction.pay_period_end,
        basis: savedBasis,
        units: deduction.units != null ? String(deduction.units) : "",
        amount: deduction.amount != null ? String(deduction.amount) : "",
        reason: deduction.reason || "",
      });
    } else {
      setEditingDeductionId(null);
      // Default to the most recent period if available.
      const periods = payPeriods?.periods || [];
      const target = periods[periods.length - 1];
      setDeductionForm({
        period_key: target ? `${target.period_start}|${target.period_end}` : "",
        pay_period_start: target?.period_start || "",
        pay_period_end: target?.period_end || "",
        basis: "custom",
        units: "",
        amount: "",
        reason: "",
      });
    }
    setDeductionPreview(null);
    setShowDeductionDialog(true);
  };

  const handleDeductionPeriodChange = (value) => {
    const [ps, pe] = value.split("|");
    setDeductionForm((d) => ({ ...d, period_key: value, pay_period_start: ps, pay_period_end: pe }));
    setDeductionPreview(null);
  };

  // Live preview: call backend to convert (basis, units) → $ amount.
  useEffect(() => {
    const { basis, units, pay_period_start, pay_period_end } = deductionForm;
    if (!showDeductionDialog || basis === "custom" || !units || !pay_period_start || !pay_period_end) {
      if (basis === "custom") setDeductionPreview(null);
      return;
    }
    const u = parseFloat(units);
    if (!(u > 0)) { setDeductionPreview(null); return; }
    const t = setTimeout(async () => {
      try {
        const res = await apiClient.get(
          `/employees/${employeeId}/deductions/preview?basis=${basis}&units=${u}&pay_period_start=${pay_period_start}&pay_period_end=${pay_period_end}`
        );
        setDeductionPreview(res.data);
      } catch { setDeductionPreview(null); }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeductionDialog, deductionForm.basis, deductionForm.units, deductionForm.pay_period_start, deductionForm.pay_period_end]);

  // ---- Balance audit ----
  const openBalanceAudit = async () => {
    setShowBalanceAudit(true);
    setLoadingAudit(true);
    setBalanceAudit(null);
    try {
      const res = await apiClient.get(`/payroll/balance-audit/${employeeId}`);
      setBalanceAudit(res.data);
    } catch (err) {
      console.error("Audit fetch failed:", err);
      toast.error(err.response?.data?.detail || "Failed to load balance audit");
      setShowBalanceAudit(false);
    } finally {
      setLoadingAudit(false);
    }
  };

  const handleSaveDeduction = async () => {
    if (!deductionForm.pay_period_start || !deductionForm.pay_period_end) {
      toast.error("Please select a pay period");
      return;
    }
    const basis = deductionForm.basis || "custom";
    let payload;
    if (basis === "custom") {
      const amt = parseFloat(deductionForm.amount);
      if (!(amt > 0)) { toast.error("Enter a deduction amount greater than zero"); return; }
      payload = {
        pay_period_start: deductionForm.pay_period_start,
        pay_period_end: deductionForm.pay_period_end,
        basis: "custom",
        amount: amt,
        reason: deductionForm.reason || undefined,
      };
    } else {
      const u = parseFloat(deductionForm.units);
      if (!(u > 0)) { toast.error(`Enter the number of ${basis.replace("_", "-")} greater than zero`); return; }
      payload = {
        pay_period_start: deductionForm.pay_period_start,
        pay_period_end: deductionForm.pay_period_end,
        basis,
        units: u,
        reason: deductionForm.reason || undefined,
      };
    }
    setSavingDeduction(true);
    try {
      if (editingDeductionId) {
        await apiClient.put(`/deductions/${editingDeductionId}`, payload);
      } else {
        await apiClient.post(`/employees/${employeeId}/deductions`, payload);
      }
      setShowDeductionDialog(false);
      setEditingDeductionId(null);
      fetchData();
      toast.success(editingDeductionId ? "Deduction updated" : "Deduction added");
    } catch (error) {
      console.error("Failed to save deduction:", error);
      toast.error(error.response?.data?.detail || "Failed to save deduction");
    } finally {
      setSavingDeduction(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-64 bg-slate-200 rounded"></div>
        </div>
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Employee not found</p>
          <Button variant="outline" onClick={() => navigate('/employees')} className="mt-4">Back to Employees</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="employee-detail-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/employees')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>

        {/* Employee Header Card */}
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src={employee.avatar} alt={employee.name} />
                  <AvatarFallback className="bg-[#0D9488] text-white text-xl">
                    {employee.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold text-[#0A192F] font-['Manrope']">{employee.name}</h1>
                    <Badge
                      variant={employee.status === 'active' ? 'default' : 'secondary'}
                      className={employee.status === 'active' ? 'bg-[#10B981] hover:bg-[#10B981]' : ''}
                    >
                      {employee.status}
                    </Badge>
                  </div>
                  <p className="text-slate-500">{employee.email || employee.phone}</p>
                  <p className="text-sm text-slate-400">ID: {employee.employee_id}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Outstanding Balance</p>
                  <p className={`text-2xl font-bold ${employee.balance?.balance > 0 ? 'text-[#E11D48]' : employee.balance?.balance < 0 ? 'text-[#10B981]' : 'text-slate-600'}`}>
                    {formatCurrency(employee.balance?.balance || 0)}
                  </p>
                  {canOverride && (
                    <button
                      type="button"
                      onClick={openBalanceAudit}
                      className="text-[11px] text-[#0D9488] hover:underline mt-1 inline-flex items-center gap-1"
                      data-testid="show-balance-math-btn"
                    >
                      <Calculator className="w-3 h-3" /> Show math
                    </button>
                  )}
                </div>
                {canOverride && (
                  employee.termination_date ? (
                    <Button variant="outline" size="sm" onClick={handleReactivate} data-testid="reactivate-employee-btn" className="border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10">
                      <UserCheck className="w-4 h-4 mr-2" /> Reactivate
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setShowTerminateDialog(true)} data-testid="terminate-employee-btn" className="border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48]/10">
                      <UserX className="w-4 h-4 mr-2" /> Terminate
                    </Button>
                  )
                )}
              </div>
            </div>
            {employee.termination_date && (
              <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-center gap-2">
                <UserX className="w-4 h-4 text-[#E11D48]" />
                <span className="text-sm text-[#E11D48] font-medium">Terminated on {formatDate(employee.termination_date)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Balance Breakdown */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Earnings", value: employee.balance?.total_earnings, color: "text-[#0A192F]" },
            { label: "Extras", value: employee.balance?.total_extras, color: "text-[#10B981]", prefix: "+" },
            { label: "Deductions", value: employee.balance?.total_deductions, color: "text-[#E11D48]", prefix: "-" },
            { label: "Total Paid", value: employee.balance?.total_paid, color: "text-[#0D9488]" },
            { label: "Balance Due", value: employee.balance?.balance, color: employee.balance?.balance > 0 ? "text-[#E11D48]" : "text-[#10B981]" },
          ].map((item) => (
            <Card key={item.label} className="border border-slate-200">
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 uppercase tracking-wider">{item.label}</p>
                <p className={`text-xl font-bold ${item.color}`}>{item.prefix || ""}{formatCurrency(item.value)}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="pay-history">Rate History</TabsTrigger>
            <TabsTrigger value="pay-periods">Pay Periods</TabsTrigger>
            <TabsTrigger value="time">Time Entries</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="deductions" data-testid="tab-deductions">Deductions</TabsTrigger>
          </TabsList>

          {/* ============ DETAILS TAB ============ */}
          <TabsContent value="details">
            <Card className="border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Employee Information</CardTitle>
                {canOverride && !editing && (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid="edit-employee-btn">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, type: "employee", id: employeeId, label: employee.name })} data-testid="delete-employee-detail-btn">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {editing ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="edit-name-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={formData.email || ""} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="edit-email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Phone</Label>
                      <Input value={formData.phone || ""} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="edit-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={formData.status} onValueChange={(val) => setFormData({...formData, status: val})}>
                        <SelectTrigger data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Pay Group</Label>
                      <Select value={formData.pay_group_id || ""} onValueChange={(val) => setFormData({...formData, pay_group_id: val})}>
                        <SelectTrigger data-testid="edit-paygroup-select"><SelectValue placeholder="Select pay group" /></SelectTrigger>
                        <SelectContent>
                          {payGroups.map(pg => (
                            <SelectItem key={pg.pay_group_id} value={pg.pay_group_id}>{pg.group_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Pay Type</Label>
                      <Select value={formData.pay_type} onValueChange={(val) => setFormData({...formData, pay_type: val})}>
                        <SelectTrigger data-testid="edit-paytype-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="hourly">Hourly</SelectItem>
                          <SelectItem value="salaried">Salaried</SelectItem>
                          <SelectItem value="daily">Daily Rate</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Pay Value</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input type="number" step="0.01" value={formData.pay_value} onChange={(e) => setFormData({...formData, pay_value: e.target.value})} className="pl-9" data-testid="edit-payvalue-input" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Hire Date</Label>
                      <Input type="date" value={formData.hire_date || ""} onChange={(e) => setFormData({...formData, hire_date: e.target.value})} data-testid="edit-hiredate-input" />
                    </div>
                    {employee.termination_date && (
                      <div className="space-y-2">
                        <Label className="text-[#E11D48]">Termination Date</Label>
                        <Input
                          type="date"
                          value={formData.termination_date || ""}
                          onChange={(e) => setFormData({...formData, termination_date: e.target.value})}
                          data-testid="edit-terminationdate-input"
                        />
                        <p className="text-xs text-slate-500">
                          Editing only adjusts the recorded date — the employee stays terminated. Use Reactivate to bring them back.
                        </p>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Schedule Group</Label>
                      <Select value={formData.schedule_group_id || "none"} onValueChange={(val) => setFormData({...formData, schedule_group_id: val === "none" ? "" : val})}>
                        <SelectTrigger data-testid="edit-schedulegroup-select"><SelectValue placeholder="No schedule" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Schedule</SelectItem>
                          {scheduleGroups.map(sg => (
                            <SelectItem key={sg.group_id} value={sg.group_id}>{sg.group_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Time Clock PIN <span className="text-slate-400 text-xs">(4-6 digits)</span></Label>
                      <Input type="text" inputMode="numeric" maxLength={6} value={formData.pin || ""} onChange={(e) => setFormData({...formData, pin: e.target.value.replace(/\D/g, "").slice(0, 6)})} placeholder="4-6 digit PIN" data-testid="edit-pin-input" />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Notes</Label>
                      <Textarea value={formData.notes || ""} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} data-testid="edit-notes-input" />
                    </div>
                    <div className="col-span-2 flex justify-end gap-2">
                      <Button variant="outline" onClick={() => { setEditing(false); setFormData(employee); }}>
                        <X className="w-4 h-4 mr-2" /> Cancel
                      </Button>
                      <Button onClick={handleSave} disabled={saving} className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="save-changes-btn">
                        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Email</p>
                      <p className="text-[#0A192F]">{employee.email || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Phone</p>
                      <p className="text-[#0A192F]">{employee.phone || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Hire Date</p>
                      <p className="text-[#0A192F]">{formatDate(employee.hire_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Pay Group</p>
                      <p className="text-[#0A192F]">{getPayGroupName(employee.pay_group_id)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Pay Type</p>
                      <p className="text-[#0A192F] capitalize">{employee.pay_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Pay Rate</p>
                      <p className="text-[#0A192F]">
                        {formatCurrency(employee.pay_value)}
                        <span className="text-slate-500 ml-1">
                          {employee.pay_type === 'hourly' ? '/hr' : employee.pay_type === 'daily' ? '/day' : '/mo'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Schedule Group</p>
                      <p className="text-[#0A192F]">{scheduleGroups.find(sg => sg.group_id === employee.schedule_group_id)?.group_name || "None"}</p>
                    </div>
                    {employee.termination_date && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Termination Date</p>
                        <p className="text-[#E11D48] font-medium">{formatDate(employee.termination_date)}</p>
                      </div>
                    )}
                    {employee.pin && (
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Clock PIN</p>
                        <p className="text-[#0A192F] font-mono">{employee.pin}</p>
                      </div>
                    )}
                    {employee.notes && (
                      <div className="col-span-2">
                        <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Notes</p>
                        <p className="text-[#0A192F]">{employee.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ============ PAY PERIODS TAB ============ */}
          <TabsContent value="pay-periods">
            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#0D9488]" /> Pay Periods
                  {payPeriods?.totals && (
                    <span className="ml-auto text-sm font-normal text-slate-500">
                      Due: <span className="font-semibold text-[#0A192F]">{formatCurrency(payPeriods.totals.total_due)}</span>
                      {' | '}Paid: <span className="font-semibold text-[#10B981]">{formatCurrency(payPeriods.totals.total_paid)}</span>
                      {' | '}Balance: <span className={`font-semibold ${payPeriods.totals.total_balance > 0 ? 'text-[#E11D48]' : 'text-[#10B981]'}`}>{formatCurrency(payPeriods.totals.total_balance)}</span>
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold w-10">Paid</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Period</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pay Type</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Rate</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Amount Due</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Paid</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Balance</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Running Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!payPeriods?.periods?.length ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">No pay periods generated. Set a hire date to see periods.</TableCell>
                      </TableRow>
                    ) : (
                      (() => {
                        let runningBalance = 0;
                        return payPeriods.periods.map((period, index) => {
                        runningBalance += period.balance;
                        const isPaidFull = period.balance <= 0 && period.amount_paid > 0;
                        const isExpanded = expandedPeriod === index;
                        return (
                          <React.Fragment key={index}>
                            <TableRow data-testid={`pay-period-row-${index}`} className={`${period.balance > 0 ? "bg-red-50/30" : isPaidFull ? "bg-emerald-50/30" : ""} ${isExpanded ? "border-b-0" : ""}`}>
                              <TableCell>
                                {canOverride && (
                                  <div className="flex items-center">
                                    {isPaidFull ? (
                                      <div data-testid={`period-paid-badge-${index}`} className="w-8 h-5 rounded-full bg-[#10B981] flex items-center justify-center cursor-default" title="Paid in full">
                                        <Check className="w-3 h-3 text-white" />
                                      </div>
                                    ) : (
                                      <Switch
                                        data-testid={`period-pay-toggle-${index}`}
                                        checked={isExpanded}
                                        onCheckedChange={() => togglePeriodPay(index, period)}
                                      />
                                    )}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="font-medium">
                                {formatDate(period.period_start)} - {formatDate(period.period_end)}
                              </TableCell>
                              <TableCell className="capitalize">{period.pay_type}</TableCell>
                              <TableCell>{formatCurrency(period.pay_rate)}<span className="text-slate-500 text-xs ml-1">{period.pay_type === 'hourly' ? '/hr' : period.pay_type === 'daily' ? '/day' : '/mo'}</span></TableCell>
                              <TableCell className="text-right font-medium text-[#0A192F]">
                                {formatCurrency(period.amount_due)}
                                {period.deduction_total > 0 && (
                                  <div className="text-[10px] text-[#E11D48]" data-testid={`period-deduction-${index}`}>
                                    -{formatCurrency(period.deduction_total)} deducted
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-right font-medium text-[#10B981]">{formatCurrency(period.amount_paid)}</TableCell>
                              <TableCell className={`text-right font-bold ${period.balance > 0 ? 'text-[#E11D48]' : period.balance < 0 ? 'text-[#10B981]' : 'text-slate-400'}`}>
                                {formatCurrency(period.balance)}
                              </TableCell>
                              <TableCell className={`text-right font-bold ${runningBalance > 0 ? 'text-[#E11D48]' : runningBalance < 0 ? 'text-[#10B981]' : 'text-slate-400'}`} data-testid={`running-balance-${index}`}>
                                {formatCurrency(runningBalance)}
                              </TableCell>
                            </TableRow>
                            {isExpanded && (
                              <TableRow data-testid={`period-pay-form-${index}`} className="bg-slate-50/80">
                                <TableCell colSpan={8} className="p-0">
                                  <div className="px-6 py-4 border-t border-dashed border-slate-300">
                                    <div className="flex items-start gap-6 flex-wrap">
                                      <div className="space-y-1.5 min-w-[140px]">
                                        <Label className="text-xs text-slate-500 uppercase tracking-wider">Payment Date</Label>
                                        <Input
                                          type="date"
                                          data-testid={`period-pay-date-${index}`}
                                          value={periodPayData.payment_date}
                                          onChange={(e) => setPeriodPayData(d => ({...d, payment_date: e.target.value}))}
                                          className="h-9 text-sm"
                                        />
                                      </div>
                                      <div className="space-y-1.5 min-w-[120px]">
                                        <Label className="text-xs text-slate-500 uppercase tracking-wider">Method</Label>
                                        <Select
                                          value={periodPayData.payment_method}
                                          onValueChange={(val) => setPeriodPayData(d => ({...d, payment_method: val}))}
                                        >
                                          <SelectTrigger data-testid={`period-pay-method-${index}`} className="h-9 text-sm">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="cash">Cash</SelectItem>
                                            <SelectItem value="zelle">Zelle</SelectItem>
                                            <SelectItem value="check">Check</SelectItem>
                                            <SelectItem value="direct_deposit">Direct Deposit</SelectItem>
                                            <SelectItem value="venmo">Venmo</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1.5 min-w-[130px]">
                                        <Label className="text-xs text-slate-500 uppercase tracking-wider">Amount</Label>
                                        <div className="relative">
                                          <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                          <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            data-testid={`period-pay-amount-${index}`}
                                            value={periodPayData.amount_paid}
                                            onChange={(e) => setPeriodPayData(d => ({...d, amount_paid: e.target.value}))}
                                            className="h-9 text-sm pl-8"
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-1.5 min-w-[130px]">
                                        <Label className="text-xs text-slate-500 uppercase tracking-wider text-[#E11D48]">Deduction</Label>
                                        <div className="relative">
                                          <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                          <Input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            data-testid={`period-pay-deduction-${index}`}
                                            value={periodPayData.deduction_amount}
                                            onChange={(e) => setPeriodPayData(d => ({...d, deduction_amount: e.target.value}))}
                                            placeholder="0.00"
                                            className="h-9 text-sm pl-8"
                                          />
                                        </div>
                                      </div>
                                      <div className="space-y-1.5 flex-1 min-w-[180px]">
                                        <Label className="text-xs text-slate-500 uppercase tracking-wider">Comments</Label>
                                        <Input
                                          data-testid={`period-pay-notes-${index}`}
                                          value={periodPayData.notes}
                                          onChange={(e) => setPeriodPayData(d => ({...d, notes: e.target.value}))}
                                          placeholder="Deduction reason, notes..."
                                          className="h-9 text-sm"
                                        />
                                      </div>
                                      <div className="flex items-end gap-2 pt-5">
                                        <Button
                                          size="sm"
                                          data-testid={`period-pay-confirm-${index}`}
                                          disabled={savingPeriodPay || !periodPayData.amount_paid}
                                          className="bg-[#0D9488] hover:bg-[#14B8A6] h-9 px-4"
                                          onClick={() => handlePeriodPaySubmit(period)}
                                        >
                                          {savingPeriodPay ? "Saving..." : "Confirm"}
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-9"
                                          onClick={() => setExpandedPeriod(null)}
                                        >
                                          <X className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      });
                      })()
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ============ PAY HISTORY TAB ============ */}
          <TabsContent value="pay-history">
            <Card className="border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-[#0D9488]" /> Rate History
                </CardTitle>
                {isOwner && (
                  <Button className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="add-pay-hike-btn" onClick={() => { setEditingPayHistoryId(null); setPayHikeData({ pay_type: employee?.pay_type || "hourly", pay_value: "", effective_date: todayLocalISO(), reason: "", unlock_pay_type: false }); setShowPayHikeDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" /> Add Pay Change
                  </Button>
                )}
              </CardHeader>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Effective Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pay Type</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Rate</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Reason</TableHead>
                      {isOwner && <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={isOwner ? 5 : 4} className="text-center py-8 text-slate-500">No pay rate history found</TableCell>
                      </TableRow>
                    ) : (
                      payHistory.map((entry, index) => (
                        <TableRow key={entry.history_id} data-testid="pay-history-row" data-highlight-id={entry.history_id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              {formatDate(entry.effective_date)}
                              {index === 0 && <Badge className="bg-[#0D9488] hover:bg-[#0D9488]">Current</Badge>}
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">{entry.pay_type}</TableCell>
                          <TableCell className="font-semibold text-[#0A192F]">
                            {formatCurrency(entry.pay_value)}
                            <span className="text-slate-500 text-sm ml-1">
                              {entry.pay_type === 'hourly' ? '/hr' : entry.pay_type === 'daily' ? '/day' : '/mo'}
                            </span>
                          </TableCell>
                          <TableCell className="text-slate-600">{entry.reason || "-"}</TableCell>
                          {isOwner && (
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" data-testid={`edit-pay-history-${entry.history_id}`} onClick={() => openEditPayHistory(entry)}>
                                  <Pencil className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button variant="ghost" size="sm" data-testid={`delete-pay-history-${entry.history_id}`} onClick={() => setDeleteDialog({ open: true, type: "pay-history", id: entry.history_id, label: `pay rate entry (${formatCurrency(entry.pay_value)} effective ${formatDate(entry.effective_date)})` })}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ============ TIME ENTRIES TAB ============ */}
          <TabsContent value="time">
            <Card className="border border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#0D9488]" /> Time Entries
                </CardTitle>
              </CardHeader>
              {(() => {
                // Group time entries by month
                const monthMap = {};
                timeEntries.forEach(entry => {
                  const d = entry.worked_date || "";
                  const monthKey = d.substring(0, 7); // YYYY-MM
                  if (!monthMap[monthKey]) monthMap[monthKey] = { entries: [], totalNet: 0, totalWorked: 0, totalBreak: 0 };
                  monthMap[monthKey].entries.push(entry);
                  monthMap[monthKey].totalNet += entry.net_hours || 0;
                  monthMap[monthKey].totalWorked += entry.worked_hours || 0;
                  monthMap[monthKey].totalBreak += entry.break_hours || 0;
                });
                const sortedMonths = Object.keys(monthMap).sort().reverse();

                if (sortedMonths.length === 0) {
                  return (
                    <CardContent>
                      <div className="text-center py-8 text-slate-500">No time entries found</div>
                    </CardContent>
                  );
                }

                return (
                  <CardContent className="p-0">
                    {sortedMonths.map((monthKey) => {
                      const group = monthMap[monthKey];
                      const [y, m] = monthKey.split('-').map(Number);
                      const label = new Date(y, m - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                      return (
                        <MonthGroup
                          key={monthKey}
                          label={label}
                          totalNet={group.totalNet}
                          totalWorked={group.totalWorked}
                          totalBreak={group.totalBreak}
                          entries={group.entries}
                          canOverride={canOverride}
                          formatDate={formatDate}
                          openEditTimeEntry={openEditTimeEntry}
                          setDeleteDialog={setDeleteDialog}
                          formatCurrency={formatCurrency}
                          highlightId={highlightId}
                        />
                      );
                    })}
                  </CardContent>
                );
              })()}
            </Card>
          </TabsContent>

          {/* ============ PAYMENTS TAB ============ */}
          <TabsContent value="payments">
            <Card className="border border-slate-200">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Period</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Method</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Amount</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Extras/Deductions</TableHead>
                      {canOverride && <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canOverride ? 6 : 5} className="text-center py-8 text-slate-500">No payments found</TableCell>
                      </TableRow>
                    ) : (
                      payments.map((payment) => (
                        <TableRow key={payment.payment_id} data-testid="payment-row" data-highlight-id={payment.payment_id}>
                          <TableCell className="font-medium">{formatDate(payment.payment_date)}</TableCell>
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(payment.pay_period_start)} - {formatDate(payment.pay_period_end)}
                          </TableCell>
                          <TableCell className="capitalize">{payment.payment_method}</TableCell>
                          <TableCell className="font-medium text-[#10B981]">{formatCurrency(payment.amount_paid)}</TableCell>
                          <TableCell>
                            {payment.extra_amount > 0 && <span className="text-[#10B981]">+{formatCurrency(payment.extra_amount)}</span>}
                            {payment.deduction_amount > 0 && <span className="text-[#E11D48] ml-2">-{formatCurrency(payment.deduction_amount)}</span>}
                            {!payment.extra_amount && !payment.deduction_amount && "-"}
                          </TableCell>
                          {canOverride && (
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" data-testid={`edit-payment-${payment.payment_id}`} onClick={() => openEditPayment(payment)}>
                                  <Pencil className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button variant="ghost" size="sm" data-testid={`delete-payment-${payment.payment_id}`} onClick={() => setDeleteDialog({ open: true, type: "payment", id: payment.payment_id, label: `payment of ${formatCurrency(payment.amount_paid)} on ${formatDate(payment.payment_date)}` })}>
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>

          {/* ============ DEDUCTIONS TAB ============ */}
          <TabsContent value="deductions">
            <Card className="border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#E11D48]" /> Deductions
                  {deductions.length > 0 && (
                    <span className="ml-2 text-sm font-normal text-slate-500">
                      Total: <span className="font-semibold text-[#E11D48]">-{formatCurrency(deductions.reduce((s, d) => s + (d.amount || 0), 0))}</span>
                    </span>
                  )}
                </CardTitle>
                {canOverride && (
                  <Button
                    size="sm"
                    onClick={() => openDeductionDialog(null)}
                    className="bg-[#0D9488] hover:bg-[#14B8A6]"
                    data-testid="add-deduction-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Deduction
                  </Button>
                )}
              </CardHeader>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Pay Period</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Amount</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Reason</TableHead>
                      {canOverride && (
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deductions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={canOverride ? 4 : 3} className="text-center py-8 text-slate-500" data-testid="deductions-empty">
                          No deductions yet. Add one to reduce Amount Due for a specific pay period.
                        </TableCell>
                      </TableRow>
                    ) : (
                      deductions.map((d) => (
                        <TableRow key={d.deduction_id} data-testid={`deduction-row-${d.deduction_id}`} data-highlight-id={d.deduction_id}>
                          <TableCell className="font-medium">
                            {formatDate(d.pay_period_start)} - {formatDate(d.pay_period_end)}
                          </TableCell>
                          <TableCell className="text-right font-medium text-[#E11D48]">
                            -{formatCurrency(d.amount)}
                          </TableCell>
                          <TableCell className="text-sm text-slate-600">{d.reason || "—"}</TableCell>
                          {canOverride && (
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeductionDialog(d)}
                                  data-testid={`edit-deduction-${d.deduction_id}`}
                                >
                                  <Pencil className="w-4 h-4 text-slate-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteDialog({ open: true, type: "deduction", id: d.deduction_id, label: `deduction of ${formatCurrency(d.amount)} for ${formatDate(d.pay_period_start)} - ${formatDate(d.pay_period_end)}` })}
                                  data-testid={`delete-deduction-${d.deduction_id}`}
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ============ PAY HISTORY DIALOG ============ */}
      <Dialog open={showPayHikeDialog} onOpenChange={(open) => { if (!open) closePayHikeDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPayHistoryId ? "Edit Pay Rate" : "Record Pay Change"}</DialogTitle>
            <DialogDescription>
              {editingPayHistoryId ? "Update this pay rate entry." : "Add a pay rate change (hike, promotion, or adjustment) for this employee."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {editingPayHistoryId && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="payhike-edit-warning">
                ⚠ Editing this entry will recalculate balances for all pay periods from <span className="font-semibold">{formatDate(payHikeData.effective_date)}</span> onward.
              </div>
            )}
            <div className="space-y-2">
              <Label>Pay Type</Label>
              {/* Default-locked to the employee's current pay type to prevent
                  accidental compensation-model swaps. Owners can deliberately
                  unlock via the checkbox below when there's a real promotion
                  (e.g., hourly contractor → salaried manager). */}
              <Select
                value={payHikeData.pay_type || employee?.pay_type || "hourly"}
                onValueChange={(val) => setPayHikeData({...payHikeData, pay_type: val})}
                disabled={!payHikeData.unlock_pay_type}
              >
                <SelectTrigger data-testid="payhike-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily Rate</SelectItem>
                  <SelectItem value="salaried">Salaried (Monthly)</SelectItem>
                </SelectContent>
              </Select>
              <label className="flex items-center gap-2 pt-1 cursor-pointer" data-testid="payhike-unlock-paytype-label">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#0D9488] focus:ring-[#0D9488]"
                  checked={Boolean(payHikeData.unlock_pay_type)}
                  onChange={(e) => setPayHikeData({
                    ...payHikeData,
                    unlock_pay_type: e.target.checked,
                    // When unlocking, default the dropdown to whatever is shown.
                    pay_type: e.target.checked ? (payHikeData.pay_type || employee?.pay_type || "hourly") : (employee?.pay_type || "hourly"),
                  })}
                  data-testid="payhike-unlock-paytype-checkbox"
                />
                <span className="text-xs text-slate-600">Override pay type (e.g., promotion to a different compensation model)</span>
              </label>
              {payHikeData.unlock_pay_type && employee && payHikeData.pay_type && payHikeData.pay_type !== employee.pay_type && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900" data-testid="payhike-type-change-warning">
                  ⚠ Switching from <span className="font-semibold">{employee.pay_type}</span> to <span className="font-semibold">{payHikeData.pay_type}</span> changes how earnings are calculated from <span className="font-semibold">{formatDate(payHikeData.effective_date)}</span> forward. Prior periods are unaffected.
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>New Rate *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="number" step="0.01" min="0" value={payHikeData.pay_value}
                  onChange={(e) => setPayHikeData({...payHikeData, pay_value: e.target.value})}
                  placeholder={employee?.pay_value?.toString() || "0.00"} className="pl-9" required data-testid="payhike-value-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Effective Date *</Label>
              <Input type="date" value={payHikeData.effective_date} onChange={(e) => setPayHikeData({...payHikeData, effective_date: e.target.value})} required data-testid="payhike-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Input value={payHikeData.reason} onChange={(e) => setPayHikeData({...payHikeData, reason: e.target.value})}
                placeholder="e.g., Annual review, Promotion, Correction" data-testid="payhike-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closePayHikeDialog}>Cancel</Button>
            <Button onClick={handleSavePayHike} disabled={savingPayHike || !payHikeData.pay_value}
              className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="save-payhike-btn">
              {savingPayHike ? "Saving..." : editingPayHistoryId ? "Update" : "Save Pay Change"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ TIME ENTRY EDIT DIALOG ============ */}
      <Dialog open={showTimeEditDialog} onOpenChange={(open) => { if (!open) { setShowTimeEditDialog(false); setEditingTimeEntry(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
            <DialogDescription>Override the time entry values.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {(() => {
              const span = clockSpanHours(editingTimeEntry);
              if (span == null) return null;
              return (
                <div
                  className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm"
                  data-testid="edit-clock-context"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-slate-700">
                      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Clocked In → Out</p>
                      <p className="mt-0.5">
                        <strong>
                          {new Date(editingTimeEntry.check_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          <span className="text-slate-400 mx-1.5">→</span>
                          {new Date(editingTimeEntry.check_out).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </strong>
                        <span className="ml-2 text-slate-500">({span.toFixed(2)}h)</span>
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-[#0D9488] text-[#0D9488] hover:bg-[#0D9488]/10 shrink-0"
                      onClick={() => setTimeEditData({ ...timeEditData, worked_hours: span.toFixed(2) })}
                      data-testid="use-clocked-hours-btn"
                    >
                      Use clocked hours
                    </Button>
                  </div>
                </div>
              );
            })()}
            <div className="space-y-2">
              <Label>Worked Date</Label>
              <Input type="date" value={timeEditData.worked_date} onChange={(e) => setTimeEditData({...timeEditData, worked_date: e.target.value})} data-testid="time-edit-date" />
            </div>
            <div className="space-y-2">
              <Label>Worked Hours</Label>
              <Input type="number" step="0.25" min="0" value={timeEditData.worked_hours}
                onChange={(e) => setTimeEditData({...timeEditData, worked_hours: e.target.value})} data-testid="time-edit-worked-hours" />
            </div>
            <div className="space-y-2">
              <Label>Break Hours</Label>
              <Input type="number" step="0.25" min="0" value={timeEditData.break_hours}
                onChange={(e) => setTimeEditData({...timeEditData, break_hours: e.target.value})} data-testid="time-edit-break-hours" />
            </div>
            {timeEditData.worked_hours && timeEditData.break_hours && (
              <p className="text-sm text-slate-500">Net Hours: <span className="font-semibold text-[#0D9488]">{(parseFloat(timeEditData.worked_hours) - parseFloat(timeEditData.break_hours)).toFixed(2)} hrs</span></p>
            )}
            {(() => {
              // Live over-clock warning inside the edit dialog. Only render
              // when the entry has both clock-in and clock-out — matches
              // the row-level badge criterion so employees can't ever be
              // surprised by a flag they didn't see while editing.
              const span = clockSpanHours(editingTimeEntry);
              if (span == null) return null;
              const typed = parseFloat(timeEditData.worked_hours || 0);
              const delta = typed - span;
              if (delta <= OVER_CLOCK_TOLERANCE_HOURS) return null;
              return (
                <div
                  className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
                  data-testid="edit-over-clock-warning"
                >
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Hours exceed the clock-in/out window</p>
                    <p className="text-xs mt-0.5">
                      Clocked span: <strong>{span.toFixed(2)}h</strong> ·
                      &nbsp;Entered: <strong>{typed.toFixed(2)}h</strong> ·
                      &nbsp;Over by <strong>+{delta.toFixed(2)}h</strong>
                    </p>
                    <p className="text-xs mt-1 text-amber-700">
                      You can still save, but this entry will show a warning flag on the row.
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowTimeEditDialog(false); setEditingTimeEntry(null); }}>Cancel</Button>
            <Button onClick={handleSaveTimeEdit} disabled={savingTimeEdit}
              className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="save-time-edit-btn">
              {savingTimeEdit ? "Saving..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ PAYMENT EDIT DIALOG ============ */}
      <Dialog open={showPaymentEditDialog} onOpenChange={(open) => { if (!open) { setShowPaymentEditDialog(false); setEditingPayment(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
            <DialogDescription>Override the payment details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Amount Paid *</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="number" step="0.01" min="0" value={paymentEditData.amount_paid}
                  onChange={(e) => setPaymentEditData({...paymentEditData, amount_paid: e.target.value})}
                  className="pl-9" data-testid="payment-edit-amount" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={paymentEditData.payment_method} onValueChange={(val) => setPaymentEditData({...paymentEditData, payment_method: val})}>
                <SelectTrigger data-testid="payment-edit-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input type="date" value={paymentEditData.payment_date} onChange={(e) => setPaymentEditData({...paymentEditData, payment_date: e.target.value})} data-testid="payment-edit-date" />
            </div>
            <div className="space-y-2">
              <Label>Pay Period</Label>
              {(() => {
                const periods = payPeriods?.periods || [];
                if (periods.length === 0) {
                  return <div className="text-xs text-slate-500 py-1">No canonical periods available — use custom dates below.</div>;
                }
                return (
                  <Select value={editPeriodSelectValue} onValueChange={handleEditPeriodSelect}>
                    <SelectTrigger data-testid="payment-edit-period-select">
                      <SelectValue placeholder="Select a pay period" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[320px]">
                      {[...periods].reverse().map(p => {
                        const key = `${p.period_start}|${p.period_end}`;
                        const isPaid = p.balance <= 0.01;
                        const status = isPaid ? "Paid" : `${formatCurrency(p.balance)} unpaid`;
                        return (
                          <SelectItem key={key} value={key} data-testid={`edit-period-option-${p.period_start}`}>
                            <span className="flex items-center justify-between gap-3 w-full">
                              <span className="font-medium">{formatDate(p.period_start)} – {formatDate(p.period_end)}</span>
                              <span className={`text-xs ${isPaid ? "text-slate-400" : "text-amber-600 font-semibold"}`}>{status}</span>
                            </span>
                          </SelectItem>
                        );
                      })}
                      <SelectItem value="__custom__" data-testid="edit-period-option-custom">
                        <span className="text-slate-600 italic">Other / custom dates…</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                );
              })()}
            </div>
            {editPeriodSelectValue === "__custom__" && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-amber-50 border border-amber-100 rounded-md">
                <div className="space-y-2">
                  <Label className="text-xs">Period Start</Label>
                  <Input type="date" value={paymentEditData.pay_period_start} onChange={(e) => setPaymentEditData({...paymentEditData, pay_period_start: e.target.value})} data-testid="payment-edit-period-start" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Period End</Label>
                  <Input type="date" value={paymentEditData.pay_period_end} onChange={(e) => setPaymentEditData({...paymentEditData, pay_period_end: e.target.value})} data-testid="payment-edit-period-end" />
                </div>
                <p className="col-span-2 text-[11px] text-amber-700">
                  ⚠️ Custom dates may not appear under their period in the Pay Periods view.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Extra Amount</Label>
                <Input type="number" step="0.01" min="0" value={paymentEditData.extra_amount}
                  onChange={(e) => setPaymentEditData({...paymentEditData, extra_amount: e.target.value})} data-testid="payment-edit-extra" />
              </div>
              <div className="space-y-2">
                <Label>Deduction Amount</Label>
                <Input type="number" step="0.01" min="0" value={paymentEditData.deduction_amount}
                  onChange={(e) => setPaymentEditData({...paymentEditData, deduction_amount: e.target.value})} data-testid="payment-edit-deduction" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={paymentEditData.notes} onChange={(e) => setPaymentEditData({...paymentEditData, notes: e.target.value})} rows={2} data-testid="payment-edit-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPaymentEditDialog(false); setEditingPayment(null); }}>Cancel</Button>
            <Button onClick={handleSavePaymentEdit} disabled={savingPaymentEdit || !paymentEditData.amount_paid}
              className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="save-payment-edit-btn">
              {savingPaymentEdit ? "Saving..." : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ BALANCE AUDIT DIALOG ============ */}
      <Dialog open={showBalanceAudit} onOpenChange={setShowBalanceAudit}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#0D9488]" />
              Balance Audit — {employee?.name}
            </DialogTitle>
            <DialogDescription>
              Every input and step behind the Outstanding Balance KPI. Spot-check each row.
            </DialogDescription>
          </DialogHeader>
          {loadingAudit ? (
            <div className="py-12 text-center text-slate-500">Loading audit…</div>
          ) : balanceAudit ? (
            <div className="space-y-4 text-sm" data-testid="balance-audit-body">
              {/* Match indicator */}
              <div className={`p-3 rounded-md border ${
                balanceAudit.matches_live
                  ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                  : "bg-amber-50 border-amber-200 text-amber-800"
              }`}>
                {balanceAudit.matches_live ? (
                  <span className="font-medium">✓ Audit matches the live calculator (diff = $0.00)</span>
                ) : (
                  <span className="font-medium">
                    ⚠ Drift detected — audit shows {formatCurrency(balanceAudit.totals.balance)} but live calculator shows {formatCurrency(balanceAudit.live_calculator.balance)} (diff {formatCurrency(balanceAudit.diff_from_live)})
                  </span>
                )}
              </div>

              {/* Earnings section */}
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-700">Earnings</div>
                <div className="divide-y divide-slate-100">
                  {balanceAudit.pay_type === "hourly" && (
                    <div className="px-3 py-2 flex justify-between" data-testid="audit-row-hourly">
                      <span>
                        Hourly entries: {balanceAudit.breakdown.hourly.entries} ×{" "}
                        <span className="font-mono">{balanceAudit.breakdown.hourly.total_hours_payable}h</span>{" "}
                        {balanceAudit.breakdown.hourly.total_hours_raw !== balanceAudit.breakdown.hourly.total_hours_payable && (
                          <span className="text-[11px] text-slate-500">
                            (raw {balanceAudit.breakdown.hourly.total_hours_raw}h, policy applied to {balanceAudit.breakdown.hourly.policy_applied_entries})
                          </span>
                        )}
                        {" "}@ {formatCurrency(balanceAudit.current_rate)}/hr
                      </span>
                      <span className="font-medium">{formatCurrency(balanceAudit.breakdown.hourly.total)}</span>
                    </div>
                  )}
                  {balanceAudit.pay_type === "salaried" && (
                    <div className="px-3 py-2 flex justify-between" data-testid="audit-row-salary">
                      <span>Salary pay periods: {balanceAudit.breakdown.salary_periods.count}</span>
                      <span className="font-medium">{formatCurrency(balanceAudit.breakdown.salary_periods.total)}</span>
                    </div>
                  )}
                  {balanceAudit.pay_type === "daily" && (
                    <div className="px-3 py-2 flex justify-between" data-testid="audit-row-daily">
                      <span>Daily entries: {balanceAudit.breakdown.daily.entries}</span>
                      <span className="font-medium">{formatCurrency(balanceAudit.breakdown.daily.total)}</span>
                    </div>
                  )}
                  <div className="px-3 py-2 flex justify-between bg-slate-50 font-semibold">
                    <span>Total Earnings</span>
                    <span>{formatCurrency(balanceAudit.totals.total_earnings)}</span>
                  </div>
                </div>
              </div>

              {/* Adjustments */}
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-700">Adjustments</div>
                <div className="divide-y divide-slate-100">
                  <div className="px-3 py-2 flex justify-between text-[#10B981]" data-testid="audit-row-extras">
                    <span>+ Extras ({balanceAudit.breakdown.extras.count})</span>
                    <span className="font-medium">+{formatCurrency(balanceAudit.totals.total_extras)}</span>
                  </div>
                  <div className="px-3 py-2 flex justify-between text-[#E11D48]" data-testid="audit-row-inline-deductions">
                    <span>− Inline deductions ({balanceAudit.breakdown.inline_deductions.count})</span>
                    <span className="font-medium">−{formatCurrency(balanceAudit.breakdown.inline_deductions.total)}</span>
                  </div>
                  <div className="px-3 py-2 flex justify-between text-[#E11D48]" data-testid="audit-row-standalone-deductions">
                    <span>− Standalone deductions ({balanceAudit.breakdown.standalone_deductions.count})</span>
                    <span className="font-medium">−{formatCurrency(balanceAudit.breakdown.standalone_deductions.total)}</span>
                  </div>
                  <div className="px-3 py-2 flex justify-between bg-slate-50 font-semibold">
                    <span>Adjusted Earnings</span>
                    <span>{formatCurrency(balanceAudit.totals.adjusted_earnings)}</span>
                  </div>
                </div>
              </div>

              {/* Payments */}
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 font-semibold text-slate-700">Payments</div>
                <div className="divide-y divide-slate-100">
                  <div className="px-3 py-2 flex justify-between text-[#0D9488]" data-testid="audit-row-payments">
                    <span>− Payments ({balanceAudit.breakdown.payments.count})</span>
                    <span className="font-medium">−{formatCurrency(balanceAudit.totals.total_paid)}</span>
                  </div>
                </div>
              </div>

              {/* Final balance */}
              <div className={`p-4 rounded-lg border-2 flex justify-between items-center ${
                balanceAudit.totals.balance > 0
                  ? "bg-red-50 border-[#E11D48]"
                  : "bg-emerald-50 border-[#10B981]"
              }`}>
                <span className="font-semibold text-lg">Balance</span>
                <span className={`text-2xl font-bold ${
                  balanceAudit.totals.balance > 0 ? "text-[#E11D48]" : "text-[#10B981]"
                }`} data-testid="audit-final-balance">
                  {formatCurrency(balanceAudit.totals.balance)}
                </span>
              </div>

              {/* Detailed line items */}
              {balanceAudit.breakdown.payments.items.length > 0 && (
                <details className="border border-slate-200 rounded-md">
                  <summary className="bg-slate-50 px-3 py-2 font-semibold text-slate-700 cursor-pointer">
                    Payment details ({balanceAudit.breakdown.payments.items.length})
                  </summary>
                  <div className="max-h-48 overflow-y-auto divide-y divide-slate-100 text-xs">
                    {balanceAudit.breakdown.payments.items.map((p, i) => (
                      <div key={i} className="px-3 py-2 flex justify-between gap-2">
                        <span className="text-slate-500">
                          {p.payment_date} · {p.method}
                          {p.period_start && (
                            <span className="ml-2 text-[10px] text-slate-400">
                              ({p.period_start} → {p.period_end})
                            </span>
                          )}
                        </span>
                        <span className="font-medium">{formatCurrency(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {balanceAudit.breakdown.standalone_deductions.items.length > 0 && (
                <details className="border border-slate-200 rounded-md">
                  <summary className="bg-slate-50 px-3 py-2 font-semibold text-slate-700 cursor-pointer">
                    Standalone deductions ({balanceAudit.breakdown.standalone_deductions.items.length})
                  </summary>
                  <div className="max-h-48 overflow-y-auto divide-y divide-slate-100 text-xs">
                    {balanceAudit.breakdown.standalone_deductions.items.map((d, i) => (
                      <div key={i} className="px-3 py-2 flex justify-between gap-2">
                        <span className="text-slate-500">
                          {d.period_start} → {d.period_end}
                          {d.reason && <span className="ml-2 italic">"{d.reason}"</span>}
                        </span>
                        <span className="font-medium text-[#E11D48]">−{formatCurrency(d.amount)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {balanceAudit.breakdown.extras.items.length > 0 && (
                <details className="border border-slate-200 rounded-md">
                  <summary className="bg-slate-50 px-3 py-2 font-semibold text-slate-700 cursor-pointer">
                    Extras ({balanceAudit.breakdown.extras.items.length})
                  </summary>
                  <div className="max-h-48 overflow-y-auto divide-y divide-slate-100 text-xs">
                    {balanceAudit.breakdown.extras.items.map((x, i) => (
                      <div key={i} className="px-3 py-2 flex justify-between gap-2">
                        <span className="text-slate-500">
                          {x.linked_payment_date}
                          {x.reason && <span className="ml-2 italic">"{x.reason}"</span>}
                        </span>
                        <span className="font-medium text-[#10B981]">+{formatCurrency(x.amount)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBalanceAudit(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DEDUCTION DIALOG ============ */}
      <Dialog open={showDeductionDialog} onOpenChange={(open) => { if (!open) { setShowDeductionDialog(false); setEditingDeductionId(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingDeductionId ? "Edit Deduction" : "Add Deduction"}</DialogTitle>
            <DialogDescription>
              Deductions reduce the Amount Due for the selected pay period.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Pay Period *</Label>
              <Select value={deductionForm.period_key} onValueChange={handleDeductionPeriodChange}>
                <SelectTrigger data-testid="deduction-period-select"><SelectValue placeholder="Select a pay period" /></SelectTrigger>
                <SelectContent>
                  {(payPeriods?.periods || []).slice().reverse().map((p) => (
                    <SelectItem key={`${p.period_start}|${p.period_end}`} value={`${p.period_start}|${p.period_end}`}>
                      {formatDate(p.period_start)} - {formatDate(p.period_end)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Calculate by *</Label>
              <Select
                value={deductionForm.basis}
                onValueChange={(v) => setDeductionForm({ ...deductionForm, basis: v, units: "", amount: "" })}
              >
                <SelectTrigger data-testid="deduction-basis-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Custom amount ($)</SelectItem>
                  <SelectItem value="days">Number of days</SelectItem>
                  <SelectItem value="half_days">Number of half-days</SelectItem>
                  <SelectItem value="hours">Number of hours</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {deductionForm.basis === "custom" ? (
              <div className="space-y-2">
                <Label>Amount *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="number" step="0.01" min="0" className="pl-9"
                    value={deductionForm.amount}
                    onChange={(e) => setDeductionForm({ ...deductionForm, amount: e.target.value })}
                    data-testid="deduction-amount-input"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>
                  Number of {deductionForm.basis === "half_days" ? "half-days" : deductionForm.basis} *
                </Label>
                <Input
                  type="number" step={deductionForm.basis === "hours" ? "0.25" : "0.5"} min="0"
                  value={deductionForm.units}
                  onChange={(e) => setDeductionForm({ ...deductionForm, units: e.target.value })}
                  placeholder={deductionForm.basis === "hours" ? "e.g., 2.5" : "e.g., 1"}
                  data-testid="deduction-units-input"
                />
                {deductionPreview && (
                  <div className="text-xs p-2 bg-slate-50 border border-slate-200 rounded" data-testid="deduction-preview">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-[#E11D48]">−{formatCurrency(deductionPreview.amount)}</span>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="text-slate-400 hover:text-slate-600 transition-colors"
                            data-testid="deduction-math-info-btn"
                            aria-label="Show calculation"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-72 text-xs" data-testid="deduction-math-popover">
                          <div className="font-semibold text-[#0A192F] mb-1.5">How this was calculated</div>
                          <div className="text-slate-600 leading-relaxed">{deductionPreview.formula}</div>
                          {deductionPreview.pay_type === "salaried" && (
                            <div className="text-[10px] text-slate-400 mt-2 pt-2 border-t border-slate-100">
                              Salary is always treated as monthly. Day count uses calendar days in the period&apos;s month. Configure Hours/Day per pay group.
                            </div>
                          )}
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="text-slate-500">{deductionPreview.note}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Daily: {formatCurrency(deductionPreview.daily_rate)} · Hourly: {formatCurrency(deductionPreview.hourly_rate)} · {deductionPreview.hours_per_workday}h workday
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Reason</Label>
              <Textarea
                rows={2}
                placeholder="e.g., Advance repayment, uniform, etc."
                value={deductionForm.reason}
                onChange={(e) => setDeductionForm({ ...deductionForm, reason: e.target.value })}
                data-testid="deduction-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowDeductionDialog(false); setEditingDeductionId(null); }}>Cancel</Button>
            <Button
              onClick={handleSaveDeduction}
              disabled={savingDeduction}
              className="bg-[#0D9488] hover:bg-[#14B8A6]"
              data-testid="save-deduction-btn"
            >
              {savingDeduction ? "Saving..." : (editingDeductionId ? "Update" : "Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ DELETE CONFIRMATION DIALOG ============ */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => { if (!open) setDeleteDialog({ open: false, type: "", id: "", label: "" }); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this {deleteDialog.label}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, type: "", id: "", label: "" })} data-testid="cancel-delete-btn">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ TERMINATE EMPLOYEE DIALOG ============ */}
      <Dialog open={showTerminateDialog} onOpenChange={setShowTerminateDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-2">
              <UserX className="w-6 h-6 text-[#E11D48]" />
            </div>
            <DialogTitle className="text-center text-[#0A192F]">Terminate {employee?.name}?</DialogTitle>
            <DialogDescription className="text-center text-slate-500 pt-2">
              Pay will be calculated only up to the termination date. The employee will be removed from schedules and marked inactive.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Termination Date *</Label>
              <Input
                type="date"
                value={terminationDate}
                onChange={(e) => setTerminationDate(e.target.value)}
                required
                data-testid="termination-date-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTerminateDialog(false)} data-testid="cancel-terminate-btn">Cancel</Button>
            <Button
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white"
              onClick={handleTerminate}
              disabled={savingTermination || !terminationDate}
              data-testid="confirm-terminate-btn"
            >
              {savingTermination ? "Processing..." : "Terminate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EmployeeDetail;
